import { AlertCallSignIcon, WarningIcon } from '@synergy/theme-next';
import {
    BonusSettlementStatus,
    UserRole,
    getBonusSettlementTranslationId,
    getBonusSettlementTypeTranslationId,
} from '@synergy/models';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { PaloColumn, PaloListView, PaloRowAction, PaloStatusPill } from '@synergy/components-palo';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolConfirmationModal } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components';
import { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AccessControl } from '../../../common/components/access-control/access-control';
import { BonusSettlement } from '../overview/models/bonus-settlement';
import { BonusSettlementPayoutModal } from '../overview/components/bonus-settlement-payout-modal';
import { GenerateBonusSettlementModal } from '../overview/components/generate-bonus-settlement-modal';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import {
    bonusSettlementsRequestingSelector,
    bonusSettlementsSelector,
} from '../overview/store/bonus-settlements-overview-selector';
import {
    createReclamation,
    deleteBonusSettlement,
    fetchBonusSettlements,
    resetBonusSettlementsState,
    updateBonusSettlement,
} from '../overview/store/bonus-settlements-overview-actions';
import { defaultSearchParamsSelector } from '../../../store/default-searchparams-selector';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { PaloFilterPanel } from '../../../common/components/palo/palo-filter-panel';
import { usePaloColumnFilters } from '../../../common/components/palo/use-palo-column-filters';
import { usePaloListViews } from '../../../common/components/palo/use-palo-list-views';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const columns: PaloColumn<BonusSettlement>[] = [
    {
        key: 'status',
        type: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        cell: item => (
            <Stack direction="row" alignItems="center" gap={1}>
                <PaloStatusPill
                    status={item.status}
                    label={<FormattedMessage id={getBonusSettlementTranslationId(item.status, item.type)} />}
                />
                {item.hasReclaimedSupplierBonusSettlements && (
                    <Tooltip title={<FormattedMessage id="app.bonusSettlement.reclaim.toolTip" />}>
                        <Box sx={{ display: 'flex' }}>
                            <AlertCallSignIcon color="error" sx={{ fontSize: 16 }} />
                        </Box>
                    </Tooltip>
                )}
            </Stack>
        ),
    },
    {
        key: 'type',
        type: 'classification',
        head: <FormattedMessage id="app.common.type" />,
        cell: item => <FormattedMessage id={getBonusSettlementTypeTranslationId(item.type)} />,
    },
    {
        key: 'year',
        type: 'count',
        head: <FormattedMessage id="app.common.year" />,
        cell: item => item.year,
    },
    {
        key: 'payoutRatio',
        type: 'percent',
        head: <FormattedMessage id="app.table.column.payoutRatio" />,
        cell: item => <FormattedNumber maximumFractionDigits={2} value={item.payout / 100} style="percent" />,
    },
    {
        key: 'sentAt',
        type: 'date',
        head: <FormattedMessage id="app.table.column.sentAt" />,
        cell: item =>
            item.sentAt ? <FormattedDate value={item.sentAt} year="numeric" month="long" day="numeric" /> : '–',
    },
];

export const PaloBonusSettlementOverview = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('bonus-settlements');
    const columnFilter = usePaloColumnFilters('bonus-settlements');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const defaultSearchParams = useAppSelector(defaultSearchParamsSelector);
    const [searchParams] = useSearchParams(defaultSearchParams);
    const bonusSettlements = useAppSelector(bonusSettlementsSelector);
    const requesting = useAppSelector(bonusSettlementsRequestingSelector);
    const [openGenerate, setOpenGenerate] = useState(false);
    const [modalData, setModalData] = useState<BonusSettlement | null>(null);
    const [toDeleteBonusSettlement, setToDeleteBonusSettlement] = useState<BonusSettlement | null>(null);

    useEffect(() => {
        dispatch(fetchBonusSettlements(searchParams));
    }, [dispatch, searchParams]);

    const openDetail = (item: BonusSettlement): void =>
        navigate(ROUTES.bonusSettlements.detail.supplier.replace(':id', item.id.toString()));

    const onDeleteBonusSettlement = (id: number) => {
        dispatch(deleteBonusSettlement({ id }));
        setToDeleteBonusSettlement(null);
    };

    const handleCreateReclamation = (item: BonusSettlement): void => {
        dispatch(createReclamation(item.id)).then(() => dispatch(fetchBonusSettlements(searchParams)));
    };

    const rowActions = (item: BonusSettlement): PaloRowAction[] => {
        const actions: PaloRowAction[] = [{ label: 'Öffnen', onClick: () => openDetail(item) }];
        if (item.hasReclaimedSupplierBonusSettlements) {
            actions.push({
                label: <FormattedMessage id="app.bonusSettlements.table.reclaim.create" />,
                onClick: () => handleCreateReclamation(item),
            });
        }
        if (item.status !== BonusSettlementStatus.Completed) {
            actions.push({
                label: <FormattedMessage id="app.bonusSettlements.table.edit" />,
                onClick: () => setModalData(item),
            });
        }
        if (item.status === BonusSettlementStatus.Draft) {
            actions.push({
                label: (
                    <FormattedMessage
                        id="app.menu.action.delete"
                        values={{ value: <FormattedMessage id="app.common.bonusSettlement" /> }}
                    />
                ),
                onClick: () => setToDeleteBonusSettlement(item),
            });
        }

        return actions;
    };

    const headerActions = (
        <AccessControl allowedRoles={UserRole.SuperAdmin}>
            <Button variant="contained" color="primary" endIcon={<WarningIcon />} onClick={() => setOpenGenerate(true)}>
                <FormattedMessage id="app.bonusSettlements.generate" />
            </Button>
        </AccessControl>
    );

    return (
        <PaloListView<BonusSettlement>
            pageTitle={intl.formatMessage({ id: 'app.navigation.bonusSettlements' })}
            title={<FormattedMessage id="app.navigation.bonusSettlements" />}
            headerActions={headerActions}
            filters={
                <>
                    <PaloFilterPanel listKey="bonus-settlements" />
                    {savedFiltersMenu}
                </>
            }
            columns={orderColumns(columns)}
            columnFilter={columnFilter}
            onMoveColumn={moveColumn}
            hiddenColumnKeys={hiddenColumnKeys}
            shownColumnKeys={shownColumnKeys}
            onToggleColumn={toggleColumn}
            items={bonusSettlements.items}
            getRowId={item => item.id}
            requesting={requesting}
            emptyLabel="Keine Bonusabrechnungen."
            onRowClick={openDetail}
            rowActions={rowActions}
        >
            <PaloNextThemeProvider>
                {/* eslint-disable @typescript-eslint/no-non-null-assertion */}
                <PoolConfirmationModal
                    open={!!toDeleteBonusSettlement}
                    title={<FormattedMessage id="app.bonusSettlements.action.remove" />}
                    onCancel={() => setToDeleteBonusSettlement(null)}
                    onConfirm={() => onDeleteBonusSettlement(toDeleteBonusSettlement!.id)}
                    action={<FormattedMessage id="app.bonusSettlements.action.remove" />}
                >
                    <PoolFormattedMessage
                        id="app.bonusSettlements.action.remove.text"
                        values={{ year: toDeleteBonusSettlement?.year }}
                    />
                </PoolConfirmationModal>
                <BonusSettlementPayoutModal
                    open={!!modalData}
                    payout={modalData?.payout}
                    onClose={() => setModalData(null)}
                    onSave={payout => {
                        if (modalData) {
                            dispatch(updateBonusSettlement({ id: modalData.id, payout }));
                        }
                        setModalData(null);
                    }}
                />
                <GenerateBonusSettlementModal
                    open={openGenerate}
                    onClose={() => setOpenGenerate(false)}
                    onGenerated={() => dispatch(fetchBonusSettlements(searchParams))}
                />
            </PaloNextThemeProvider>
        </PaloListView>
    );
};
